import api from "../../../api/axios";
import { getLoanBalanceForSellerNetSheet } from "../../../utils/calculator";
import { shouldEstimateLoanBalance } from "../../../utils/autoEstimateLoan";

/**
 * mapTtb - Map raw API TTB data to standardized format
 * @param {Object} raw - Raw API response
 * @param {string} mode - Calculator mode
 * @param {string} siteUnitUpdated - Updated site unit value
 * @returns {Object} Mapped TTB data
 */
function mapTtb(raw, mode, siteUnitUpdated) {
  const cs = raw?.data || {};
  const ttb = cs?.ttb || {};

  return {
    apn: ttb.apn || "NA",
    mode: mode || "",
    ownername: ttb.ownername || "NA",
    type: ttb.type || "NA",
    bed: ttb.bed || "NA",
    bath: ttb.bath || "NA",
    sqft: ttb.sqft || "NA",
    built: ttb.built || "NA",
    county_name: cs.county_name || "",
    state_name: cs.state_name || "",
    state_code: cs.state_code || "",
    stateid: cs.stateid || "",
    countyid: cs.countyid || "",
    assessedValue: raw?.assessedValue ?? ttb.assessedValue ?? "",
    caTaxRate: raw?.caTaxRate ?? ttb.caTaxRate ?? "",
    adjustedCashPercent: raw?.caTaxRate ?? ttb.adjCashPer ?? "",
    inflationRate: raw?.caTaxRate ?? ttb.infiRate ?? "",
    taxAmount: raw?.taxAmount ?? ttb.taxAmount ?? "",
    taxYear: raw?.taxYear ?? ttb.taxYear ?? "",
    site_unit: siteUnitUpdated || (raw?.site_unit ?? ttb.site_unit ?? ""),
  };
}

/**
 * fetchCityStateForZip - Common API call for get-city-state-for-zip
 * Used by setUpPlaceDetails (autocomplete), handleAddressSearch (manual search), and handleParcelSelected
 * @param {Object} payload - API request params (street_no, route_long, city, zip, etc.)
 * @param {Object} options - { controller, mode }
 * @returns {Promise<{success: boolean, ttbData: Object|null, error: string|null, payload: Object, raw: Object}>}
 */
export async function fetchCityStateForZip(payload, { controller, mode } = {}) {
 // console.log("fetchCityStateForZip get-city-state-for-zip api call");
  const resp = await api.get("/get-city-state-for-zip", {
    params: payload,
    signal: controller?.signal,
  });

  const raw = resp?.data ?? null;
  const cs = raw?.data || {};
  const ttbRaw = cs?.ttb || {};
  const ttbStatus = ttbRaw?.status !== undefined ? Number(ttbRaw.status) : undefined;

  // Normalize payload with response data
  const normalizedPayload = {
    ...payload,
    city: raw?.city || payload.city || "",
    state_name: cs.state_name || payload.state_name || "",
    state_code: cs.state_code || payload.state_code || "",
    closing_county: cs.closing_county || payload.closing_county || "",
    closing_state: cs.state_code || payload.closing_state || "",
    companyid:cs.companyid || payload.companyid || "",
    county_name: cs.county_name || payload.county_name || "",
    countyid: cs.countyid || payload.countyid || "",
    stateid: cs.stateid || payload.stateid || "",
  };
//console.log("normalizedPayload",normalizedPayload);
  // Check if property address is valid based on API status
  // Success: status === "success" with populated data
  // Fail: status === "fail" (even with empty data object)
  const isSuccess = raw?.status === "success";

  if (!isSuccess) {
    return {
      success: false,
      ttbData: null,
      error: raw?.message || "Property not found or not supported",
      payload: normalizedPayload,
      raw,
    };
  }

  // Process TTB data when API returns success
  let resolvedTtb = {};

  if (ttbStatus !== 0) {
    // Process TTB data using mapTtb function
    const siteUnitUpdated = payload.site_unit || "";
    resolvedTtb = mapTtb(raw, mode || "", siteUnitUpdated);

    // Add existing-loan balance estimate if applicable (seller/refinance —
    // see shouldEstimateLoanBalance for why)
    if (shouldEstimateLoanBalance(mode)) {
      try {
        const loanBalance = getLoanBalanceForSellerNetSheet(
          ttbRaw?.loanAmount,
          cs?.loanRate || 0,
          30,
          ttbRaw?.loanDate
        );
        if (loanBalance) resolvedTtb.loanBalance = loanBalance;
      } catch {}
    }
  }

  return {
    success: true,
    ttbData: resolvedTtb,
    error: null,
    payload: normalizedPayload,
    raw,
  };
}

/**
 * fetchStreetViewCamera - Get the Street View camera (panoId/heading/zoom) the
 * backend also uses for the PDF, so the interactive panel and the printout
 * show the identical view — don't pick a panorama in the browser.
 * Pass the exact address string later sent to the PDF (data.addressMeta.address).
 * @param {string} address
 * @param {Object} [options] - { controller }
 * @returns {Promise<{available:boolean, panoId?:string, heading?:number|null, pitch?:number, fov?:number, zoom?:number, panoLocation?:{lat,lng}, propertyLocation?:{lat,lng}|null}>}
 */
export async function fetchStreetViewCamera(address, { controller } = {}) {
  try {
    const resp = await api.get("/street-view", {
      params: { address },
      signal: controller?.signal,
    });
    if (resp?.data?.status === "success" && resp.data.data) {
      return resp.data.data;
    }
    return { available: false };
  } catch (error) {
    if (error?.name === "CanceledError" || error?.code === "ERR_CANCELED") {
      throw error;
    }
    return { available: false };
  }
}